import React from 'react';

import type { DomSessionRow } from '@/types/domSession';

import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { StatusBadge } from '@/components/platform/StatusBadge';

interface SessionsListProps {
  sessions: DomSessionRow[];
  selectedSession: DomSessionRow | null;
  isLoading: boolean;
  onSessionSelect: (session: DomSessionRow) => void;
  onSessionHover?: (session: DomSessionRow) => void;
}

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const SessionsList: React.FC<SessionsListProps> = ({
  sessions,
  selectedSession,
  isLoading,
  onSessionSelect,
  onSessionHover,
}) => {
  return (
    <div className="h-full bg-transparent">
      <ScrollArea className="h-full">
        {isLoading && !sessions.length && (
          <div className="space-y-0">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="border-b border-border/70 px-3 py-3">
                <Skeleton className="mb-2 h-4 w-48" />
                <Skeleton className="h-3 w-56" />
              </div>
            ))}
          </div>
        )}

        {!isLoading && !sessions.length && (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
            No sessions yet.
          </div>
        )}

        <div>
          {sessions.map((session) => {
            const isSelected = selectedSession?.id === session.id;
            const email = session.userEmail || 'Anonymous';
            const salesSummary = (session as any).sales_summary;
            const updatedQualificationStatus =
              salesSummary?.updated_qualification_status ?? (session as any).updated_qualification_status;
            const qualificationStatus =
              updatedQualificationStatus ??
              salesSummary?.qualification_status ??
              (session as any).qualification_status;
            const name = salesSummary?.name || null;
            const date = formatDate(session.started_at);
            const durationS = session.duration_s;
            const duration = durationS != null
              ? durationS >= 60 ? `${Math.floor(durationS / 60)}m ${Math.round(durationS % 60)}s` : `${Math.round(durationS)}s`
              : null;

            const bookedStatus = salesSummary?.booked_status ?? (session as any).booked_status;
            const isBooked = bookedStatus === 'booked';
            const sourceEnvironment = salesSummary?.source_environment ?? session.source_environment ?? 'production';

            const qualBadge = qualificationStatus === 'qualified'
              ? { label: 'Qualified', tone: 'success' as const }
              : qualificationStatus === 'unqualified'
                ? { label: 'Not qualified', tone: 'danger' as const }
                : null;
            return (
              <button
                key={session.id}
                type="button"
                onClick={() => onSessionSelect(session)}
                onMouseEnter={() => onSessionHover?.(session)}
                className={cn(
                  'min-h-[68px] w-full border-b border-border/70 px-4 py-4 text-left transition-colors',
                  isSelected
                    ? 'bg-accent/35'
                    : 'hover:bg-muted/50'
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-foreground">
                      {name || email}
                    </div>
                    <div className="mt-1 truncate text-[12px] text-muted-foreground">
                      {name ? email : ''}{name ? ' · ' : ''}{date}{duration ? ` · ${duration}` : ''}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    {sourceEnvironment === 'staging' && (
                      <StatusBadge tone="warning" className="max-w-[88px]">
                        Staging
                      </StatusBadge>
                    )}
                    {isBooked && (
                      <StatusBadge tone="info" className="max-w-[88px]">
                        Booked
                      </StatusBadge>
                    )}
                    {qualBadge && (
                      <StatusBadge tone={qualBadge.tone} className="max-w-[100px]">
                        {qualBadge.label}
                      </StatusBadge>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

export default SessionsList;
